/* global React, Icon, Switch, useState, useEffect, t */
// Delt forslagspanel mot foretaksregisteret. Brukes av leverandorkortet,
// kundekortet og «ny kunde» i salgsflyten.
//
// Panelet foreslaar, det krever ingenting: en privatkunde skriver bare navnet
// og lar panelet vaere. `quiet` slaar av «ingen treff»-linjen for skjemaer der
// privatpersoner er like vanlige som foretak.
function BusinessRegistryLookup({ query, onApply, country, embedded = false, quiet = false }) {
  const registry = window.PlummeBusinessRegistry;
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Etter at et treff er brukt, fylles org.nr.-feltet med treffets nummer.
  // Uten dette ville panelet umiddelbart slaa opp det samme foretaket igjen.
  // Samme grunn til aa starte med et ferdig utfylt org.nr. som avvist: aa aapne
  // et lagret kundekort skal ikke foresla kunden sin egen oppforing paa nytt.
  const [dismissedOrg, setDismissedOrg] = useState(() => {
    const digits = String(query || '').replace(/\D/g, '');
    return digits.length === 9 ? digits : '';
  });
  const lookupCountry = String(country || registry.country()).toUpperCase();
  const plan = registry.plan(query, { country: lookupCountry, dismissedOrg });
  const searchFor = plan.action === 'search' ? plan.query : '';
  useEffect(() => {
    if (!searchFor || !window.PlummeBackend?.lookupBusinessRegistry) {
      setMatches([]);
      setBusy(false);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setBusy(true);
      setError('');
      try {
        const result = await window.PlummeBackend.lookupBusinessRegistry(searchFor, lookupCountry);
        if (!cancelled) setMatches(Array.isArray(result) ? result.slice(0, 6) : []);
      } catch (err) {
        if (!cancelled) {
          setMatches([]);
          setError(err?.message || t('registry.failed', 'BRREG-oppslag feilet.'));
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 260);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchFor, lookupCountry]);
  if (plan.action === 'off') return null;
  const waitingForOrgNumber = plan.action === 'wait-org';
  if (!busy && !matches.length && !error && !waitingForOrgNumber) return null;
  const content = <React.Fragment>
    {waitingForOrgNumber && <div className="combo-empty t-sm muted">{t('registry.needNineDigits', 'Skriv 9 siffer for å søke org.nr. i Brreg.')}</div>}
    {busy && <div className="combo-empty t-sm muted">{t('registry.searching', 'Søker i Brreg...')}</div>}
    {!busy && matches.map((match) => <button key={match.organizationNumber || match.id || match.name} type="button" className="combo-item" onClick={() => {
      setDismissedOrg(String(match.organizationNumber || '').replace(/\D/g, ''));
      setMatches([]);
      onApply && onApply(match);
    }}>
      <span><strong>{match.businessName || match.name}</strong><small className="muted" style={{ display: 'block', marginTop: 2 }}>{[match.organizationNumber, registry.formatAddress(match)].filter(Boolean).join(' · ')}</small></span>
      <Icon name="arrowRight" size={16} />
    </button>)}
    {!busy && !matches.length && !error && !quiet && <div className="combo-empty t-sm muted">{t('registry.noHits', 'Ingen treff i Brreg.')}</div>}
    {error && <div className="combo-empty t-sm" style={{ color: 'var(--error)', fontWeight: 800 }}>{error}</div>}
  </React.Fragment>;
  // The panel spans the form grid itself. Callers wrap it in a display:contents div, so when there is
  // nothing to show (this returns null above) no empty grid item is left behind adding an extra row gap.
  return embedded ? content : <div className="combo-pop purchase-supplier-pop" style={{ position: 'static', marginTop: 6, gridColumn: '1 / -1' }}>{content}</div>;
}

// Av/paa for registersoket. Uten den maa en privatkunde enten velge et foretak
// den ikke er, eller leve med et forslagspanel som ikke gaar vekk.
function BusinessRegistryToggle({ on, onChange, country, help }) {
  const registry = window.PlummeBusinessRegistry;
  if (String(country || registry.country()).toUpperCase() !== 'NO') return null;
  return <div className="row between gap-12" style={{ alignItems: 'center' }}>
    <span style={{ minWidth: 0 }}>
      <b className="t-sm">{t('registry.toggle', 'Søk i Brreg')}</b>
      <span className="t-xs muted" style={{ display: 'block', fontWeight: 600 }}>{help || t('registry.toggleHelp', 'Foreslår foretak mens du skriver. Slå av for privatkunder.')}</span>
    </span>
    <Switch on={on} onChange={onChange} />
  </div>;
}

// Skjemaene deler valget: `useBusinessRegistryToggle` holder av/paa-tilstanden
// og husker den til neste gang.
function useBusinessRegistryToggle() {
  const registry = window.PlummeBusinessRegistry;
  const [enabled, setEnabled] = useState(() => registry.lookupEnabled());
  return [enabled, (value) => setEnabled(registry.setLookupEnabled(value))];
}

Object.assign(window, { BusinessRegistryLookup, BusinessRegistryToggle, useBusinessRegistryToggle });
